// canvas/cream-board.js: restyle step 2, the cream table's canvas.
//
// Draws the board (green felt, dark outline, inner line), the four trick
// cells, the trick cards (resting, sliding in, sweeping to the winner),
// the up-card in the dealer's cell, and your hand. Text (seat tags, cell
// names, the WIN badge, the controls row) is DOM over the canvas, placed
// from the same layout (cream-layout.js), so it stays on the pixel font.
//
// Everything is in board coordinates (CSS px from the board's corner).
// Returns what it drew, so the view can place hit zones and browser
// tests can read the cards (data-drawn-cards).

import { renderCard, isBower, isTrumpCard } from './cards.js';
import { handCardRects, slotOfSeat } from './cream-layout.js';
import { _internals as trickArea } from './trick-area.js';

// cream.css: --table, --table-line, --outline, --win-ring.
export const COLORS = {
  table: '#2f6355',
  tableLine: '#447f6d',
  outline: '#261a22',
  winRing: '#fffaf0',
  pickup: '#e8ad45',
  select: '#e8ad45',
};
const BOARD_RADIUS = 20;
const CELL_RADIUS = 10;
export const HOVER_LIFT = 6;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Board: a 5 px dark outline, green felt, a 3 px lighter inner line. */
function drawBoard(ctx, L) {
  const w = L.board.w;
  const h = L.board.h;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = COLORS.outline;
  roundRect(ctx, 0, 0, w, h, BOARD_RADIUS);
  ctx.fill();
  const b = L.bw;
  ctx.fillStyle = COLORS.table;
  roundRect(ctx, b, b, w - 2 * b, h - 2 * b, BOARD_RADIUS - b);
  ctx.fill();
  ctx.strokeStyle = COLORS.tableLine;
  ctx.lineWidth = 3;
  roundRect(ctx, b + 1.5, b + 1.5, w - 2 * b - 3, h - 2 * b - 3, BOARD_RADIUS - b - 1.5);
  ctx.stroke();
}

/** An empty trick cell: a 3 px inner line, rounded. */
function drawCell(ctx, r) {
  ctx.strokeStyle = COLORS.tableLine;
  ctx.lineWidth = 3;
  roundRect(ctx, r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3, CELL_RADIUS - 1.5);
  ctx.stroke();
}

const center = (r) => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });

/**
 * @typedef {{
 *   viewerSeat: number,
 *   snap: any,
 *   frame: null | { trick_plays: Array<{seat:number, card_id:string}>,
 *     slide: {seat:number, card_id:string, t:number}|null,
 *     trick_resolution: {winner_seat:number, elapsed_ms:number}|null },
 *   liveSlide: {seat:number, card_id:string, t:number}|null,
 *   winnerSeat: number|null,
 *   hover: {card_id:string, lift:number}|null,
 *   selectedCardId: string|null,
 *   legalPlays?: string[]|null,
 *   upcard: null | { card_id: string, slot: string, mode: 'bid'|'turned'|'pickup', selected?: boolean, intro?: number },
 *   tagCenters: Record<string, {x:number, y:number}>,
 * }} BoardModel
 */

/**
 * Draw the board and return the drawn cards (board coordinates).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('./cream-layout.js').CreamLayout} L
 * @param {BoardModel} m
 */
export function renderCreamBoard(ctx, L, m) {
  ctx.imageSmoothingEnabled = false;
  drawBoard(ctx, L);
  for (const r of Object.values(L.cells)) drawCell(ctx, r);
  const snap = m.snap || {};
  const trump = snap.trump_suit || null;

  // Up-card in the dealer's cell (bidding, the dealer's pickup).
  /** @type {null|{card_id:string, x:number, y:number, w:number, h:number}} */
  let upcard = null;
  if (m.upcard) {
    const r = L.cells[m.upcard.slot];
    if (r) {
      const id = m.upcard.card_id;
      const t = m.upcard.intro ?? 1;
      if (t < 1) {
        // Hand start: the card springs in from a little smaller.
        const s = 0.8 + 0.2 * t;
        ctx.save();
        ctx.translate(r.x + r.w / 2, r.y + r.h / 2);
        ctx.scale(s, s);
        renderCard(ctx, id, -r.w / 2, -r.h / 2, r.w, r.h, { face_up: true, trump: isTrumpCard(id, trump) });
        ctx.restore();
      } else {
        renderCard(ctx, id, r.x, r.y, r.w, r.h, {
          face_up: true,
          // Round two: the turned-down card shows greyed.
          grayed: m.upcard.mode === 'turned',
          trump: isTrumpCard(id, trump),
          hover: !!m.upcard.selected,
        });
      }
      if (m.upcard.mode === 'pickup' && !m.upcard.selected) {
        ctx.save();
        ctx.strokeStyle = COLORS.pickup;
        ctx.lineWidth = 3;
        roundRect(ctx, r.x - 3.5, r.y - 3.5, r.w + 7, r.h + 7, 11);
        ctx.stroke();
        ctx.restore();
      }
      upcard = { card_id: id, x: r.x, y: r.y, w: r.w, h: r.h };
    }
  }

  // Trick cards.
  const drawnTrick = drawTrick(ctx, L, m, trump);

  // Your hand.
  const drawnHand = drawHand(ctx, L, m, trump);
  return { hand: drawnHand, trick: drawnTrick, upcard };
}

function drawTrick(ctx, L, m, trump) {
  const frame = m.frame;
  const plays = frame ? frame.trick_plays : (m.snap?.trick_plays || []);
  const slide = frame ? frame.slide : m.liveSlide;
  const res = frame ? frame.trick_resolution : null;
  /** @type {any[]} */
  const drawn = [];
  // The pace part's timing (trick-area.js, the same sweep the sequencer
  // runs): the sweep takes SWEEP_DURATION_MS, inside the 490 ms sequence.
  // As in the mockup, no card grows: the trick-win pop is the score
  // block's squash and "+1 TRICK" (DOM, ui/CreamTable.jsx ScoreBlock).
  const { SWEEP_DURATION_MS } = trickArea;
  for (const p of plays) {
    const slot = slotOfSeat(Number(p.seat), m.viewerSeat);
    const cell = slot ? L.cells[slot] : null;
    if (!cell) continue;
    let { x, y } = cell;
    let { w, h } = cell;
    let resting = true;
    const isWinner = m.winnerSeat != null && Number(p.seat) === m.winnerSeat;
    if (slide && slide.seat === Number(p.seat) && slide.card_id === p.card_id && !res) {
      // Slide in from the seat's tag (or from your hand).
      const from = slideFrom(L, m, slot);
      const t = Math.max(0, Math.min(1, slide.t));
      x = Math.round(from.x + (cell.x - from.x) * t);
      y = Math.round(from.y + (cell.y - from.y) * t);
      resting = t >= 1;
    }
    if (res) {
      // Sweep to the winner's tag, shrinking to 40% (the mockup's
      // sweepOut), every card the same way.
      const target = m.tagCenters[slotOfSeat(res.winner_seat, m.viewerSeat) ?? ''] ?? center(cell);
      const t = Math.min(1, res.elapsed_ms / SWEEP_DURATION_MS);
      const e = t * t;
      const scale = 1 - 0.6 * e;
      const c = center(cell);
      const cx = c.x + (target.x - c.x) * e;
      const cy = c.y + (target.y - c.y) * e;
      w = Math.max(8, Math.round(cell.w * scale));
      h = Math.max(11, Math.round(cell.h * scale));
      x = Math.round(cx - w / 2);
      y = Math.round(cy - h / 2);
      resting = false;
    }
    // A card in flight never leaves the felt (the canvas would cut it).
    x = Math.max(L.felt.x, Math.min(x, L.felt.x + L.felt.w - w));
    y = Math.max(L.felt.y, Math.min(y, L.felt.y + L.felt.h - h));
    ctx.save();
    if (isWinner) {
      // The winner's light outer frame (mockup .trick-card.winner).
      ctx.strokeStyle = COLORS.winRing;
      ctx.lineWidth = 4;
      roundRect(ctx, x - 4, y - 4, w + 8, h + 8, 12);
      ctx.stroke();
    }
    const isTrump = isTrumpCard(p.card_id, trump);
    renderCard(ctx, p.card_id, x, y, w, h, { face_up: true, trump: isTrump });
    ctx.restore();
    drawn.push({ card_id: p.card_id, seat: Number(p.seat), slot, x, y, w, h, resting, winner: isWinner, trump: isTrump });
  }
  const el = ctx.canvas;
  if (el && el.dataset) {
    // data-trick-landed counts the cards at rest in their cells (slid
    // in, not sweeping), so a pace test can time each landing.
    if (drawn.length) {
      el.dataset.trickCards = drawn.map((d) => `${d.x},${d.y},${d.w},${d.h}`).join(';');
      el.dataset.trickLanded = String(drawn.filter((d) => d.resting).length);
    } else {
      delete el.dataset.trickCards;
      delete el.dataset.trickLanded;
    }
  }
  return drawn;
}

/** The shortest slide into a trick cell, CSS px (the mockup's fit check C9). */
const MIN_SLIDE = 60;

/**
 * Where a card starts its slide: your hand card's spot, or the seat's tag,
 * on the felt (a card in flight never leaves it) and at least MIN_SLIDE
 * from its cell.
 */
function slideFrom(L, m, slot) {
  const cell = L.cells[slot];
  if (slot === 'S') {
    const n = (m.snap?.hands?.[m.viewerSeat]?.length ?? 0) + 1;
    const rects = handCardRects(L, Math.max(1, Math.min(5, n)));
    const r = rects[Math.floor(rects.length / 2)];
    return { x: r.x + Math.round((r.w - cell.w) / 2), y: r.y };
  }
  const c = m.tagCenters[slot];
  if (!c) return { x: cell.x, y: cell.y };
  const f = L.felt;
  const onFelt = (p) => ({
    x: Math.max(f.x, Math.min(p.x, f.x + f.w - cell.w)),
    y: Math.max(f.y, Math.min(p.y, f.y + f.h - cell.h)),
  });
  const from = onFelt({ x: Math.round(c.x - cell.w / 2), y: Math.round(c.y - cell.h / 2) });
  const dx = from.x - cell.x;
  const dy = from.y - cell.y;
  // Slide in along the tag's main direction, as far as the felt allows.
  // Where that is under MIN_SLIDE (the partner's cell on small phones, with
  // the felt's edge close above it), add the missing travel across it, at
  // most 45 degrees, away from the trick block's middle. So the card comes
  // from its seat and never crosses another cell: an empty cell's name
  // label sits above the canvas and would show on the card.
  const vertical = Math.abs(dy) >= Math.abs(dx);
  const along = vertical ? dy : dx;
  const n = Math.abs(along);
  const across = n >= MIN_SLIDE ? 0 : Math.min(n, Math.ceil(Math.sqrt(MIN_SLIDE * MIN_SLIDE - n * n)));
  const cells = Object.values(L.cells);
  const mid = vertical
    ? cells.reduce((s, k) => s + k.x + k.w / 2, 0) / cells.length - (cell.x + cell.w / 2)
    : cells.reduce((s, k) => s + k.y + k.h / 2, 0) / cells.length - (cell.y + cell.h / 2);
  const sign = mid > 0 ? -1 : 1;
  return onFelt(vertical
    ? { x: cell.x + sign * across, y: cell.y + along }
    : { x: cell.x + along, y: cell.y + sign * across });
}

function drawHand(ctx, L, m, trump) {
  const snap = m.snap || {};
  const seat = m.viewerSeat;
  const hand = (snap.hands && snap.hands[seat]) || [];
  if (!hand.length || snap.sitting_out_seat === seat) return [];
  const rects = handCardRects(L, hand.length);
  // Your legal cards on your trick turn: the server's your_legal_plays
  // (m.legalPlays), else a per-seat legal_plays map (tests, the dev table).
  const legalList = snap.current_state !== 'trick_play' ? null
    : Array.isArray(m.legalPlays) ? m.legalPlays
      : snap.legal_plays ? snap.legal_plays[seat] || [] : null;
  const legalSet = legalList ? new Set(legalList) : null;
  /** @type {any[]} */
  const out = [];
  hand.forEach((cardId, i) => {
    const r = rects[i];
    const legal = legalSet === null ? true : legalSet.has(cardId);
    const grayed = legalSet !== null && !legal;
    const selected = m.selectedCardId === cardId;
    const lift = selected ? HOVER_LIFT : (m.hover && m.hover.card_id === cardId && legal ? Math.round(m.hover.lift) : 0);
    const bower = isBower(cardId, trump);
    const isTrump = isTrumpCard(cardId, trump);
    renderCard(ctx, cardId, r.x, r.y - lift, r.w, r.h, {
      face_up: true, grayed, bower, trump: isTrump, hover: selected,
    });
    out.push({ card_id: cardId, x: r.x, y: r.y - lift, w: r.w, h: r.h, legal, bower, grayed, trump: isTrump, lift });
  });
  return out;
}

export const _internals = { drawBoard, drawCell, slideFrom };
